import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { Marker, Popup, GeoJSON, useMap } from "react-leaflet";
import L from "leaflet";
import type { Map as LeafletMap } from "leaflet";
import type { GeoJsonObject } from "geojson";
import { Link, useSearchParams } from "react-router-dom";
import FloraIQMap from "../../components/shared/FloraIQMap";
import MapToolbar from "./components/MapToolbar";
import MapFilterPanel, { type MapFilterOptions } from "./components/MapFilterPanel";
import MapLegend from "./components/MapLegend";
import MapStatsCards from "./components/MapStatsCards";
import MapDetailPanel from "./components/MapDetailPanel";
import MapSearchBar from "./components/MapSearchBar";
import MapLayerControl from "./components/MapLayerControl";
import MapGISTools from "./components/MapGISTools";
import MapScaleAndPointer from "./components/MapScaleAndPointer";
import { pinWidth, treePinSvg } from "./components/treePins";
import { treePinState } from "@/config/tree-pin-states";
import MunicipalTreesLayer, { MUNICIPAL_COLOR, type MunicipalLayerStatus } from "./components/MunicipalTreesLayer";
import { api } from "@/lib/api";
import { useAreasPiloto, useInspectionsList, useQuadrasList } from "@/hooks";
import { toTreeMapPoint, type MapLayer, type TreeMapPoint } from "@/types/cta-mapa-ui";
import { riscoColor, riscoLabel, SBV_AREA_PILOTO_CODE, SBV_CENTER } from "@/config/cta-mapa-config";

const INITIAL_LAYERS: MapLayer[] = [
  { id: "arvores", nome: "Árvores", icone: "ri-plant-line", cor: "#1B6E4F", ativa: true, categoria: "individuos" },
  { id: "palmeiras", nome: "Palmeiras", icone: "ri-leaf-line", cor: "#2E8B57", ativa: true, categoria: "individuos" },
  { id: "mudas", nome: "Mudas", icone: "ri-seedling-line", cor: "#4CAF50", ativa: true, categoria: "individuos" },
  { id: "outros", nome: "Outros tipos (toco, morto, não identificado)", icone: "ri-question-line", cor: "#9ca3af", ativa: true, categoria: "individuos" },
  { id: "area_piloto", nome: "Área piloto (Setor Bela Vista)", icone: "ri-shape-line", cor: "#1B6E4F", ativa: true, categoria: "territorio" },
  { id: "quadras", nome: "Quadras da área piloto", icone: "ri-grid-line", cor: "#2563eb", ativa: true, categoria: "territorio" },
  { id: "limite_municipal", nome: "Limite do município (IBGE 2022)", icone: "ri-map-2-line", cor: "#111827", ativa: true, categoria: "territorio" },
  { id: "regioes", nome: "Regiões administrativas", icone: "ri-compass-3-line", cor: "#b45309", ativa: false, categoria: "territorio" },
  { id: "bairros", nome: "Bairros oficiais", icone: "ri-community-line", cor: "#0891b2", ativa: false, categoria: "territorio" },
  {
    id: "arvores_municipais",
    nome: "Árvores da Prefeitura (1998–2000)",
    descricao: "Cadastro municipal, não avaliado pelo FloraIQ",
    icone: "ri-government-line",
    cor: MUNICIPAL_COLOR,
    ativa: false,
    categoria: "referencia",
  },
];

function layerOfTree(tipo: string): string {
  switch (tipo) {
    case "ARVORE": return "arvores";
    case "PALMEIRA": return "palmeiras";
    case "MUDA": return "mudas";
    default: return "outros";
  }
}

function makeTreeIcon(risco: string, tipo: string, selected: boolean): L.DivIcon {
  // Arvore usa o pin da situacao (catalogo MAP_PIN_STATES); palmeira, muda e outros, o desenho do tipo.
  const imagePin = tipo === "ARVORE";
  const width = Math.round((imagePin ? 28 : pinWidth(tipo)) * (selected ? 1.3 : 1));
  const height = Math.round(imagePin ? (width * 96) / 84 : (width * 40) / 32);
  const state = treePinState(risco);
  const html = imagePin
    ? `<img src="${state.icon}" alt="${state.label}" width="${width}" height="${height}" draggable="false"
        style="display:block;filter:drop-shadow(0 1px 2px rgba(0,0,0,.35))${selected ? " drop-shadow(0 0 3px #3b82f6) drop-shadow(0 0 2px #3b82f6)" : ""}"/>`
    : treePinSvg(tipo, riscoColor(risco), selected, width);
  return L.divIcon({
    html,
    className: "",
    iconSize: [width, height],
    // A ponta da gota marca a posicao do tronco.
    iconAnchor: [width / 2, height - 1],
    popupAnchor: [0, -height + 4],
  });
}

function fitTo(map: LeafletMap, trees: TreeMapPoint[], fallback: GeoJsonObject | null) {
  if (trees.length > 0) {
    map.fitBounds(L.latLngBounds(trees.map((t) => [t.latitude, t.longitude])), { padding: [40, 40], maxZoom: 17 });
  } else if (fallback) {
    const b = L.geoJSON(fallback).getBounds();
    if (b.isValid()) map.fitBounds(b, { padding: [20, 20] });
  }
}

/** Enquadra uma unica vez, quando o primeiro dado real chega.
 *  Com focusId (vindo de "Abrir no mapa" da FCA), aproxima naquele exemplar e o seleciona. */
function FitOnce({
  trees, fallback, ready, focusId, onFocus,
}: {
  trees: TreeMapPoint[];
  fallback: GeoJsonObject | null;
  ready: boolean;
  focusId?: number | null;
  onFocus?: (tree: TreeMapPoint | null) => void;
}) {
  const map = useMap();
  const fitted = useRef(false);
  useEffect(() => {
    if (fitted.current || !ready) return;
    if (focusId != null) {
      fitted.current = true;
      const tree = trees.find((t) => t.id === focusId) ?? null;
      if (tree) {
        map.setView([tree.latitude, tree.longitude], 19);
      } else {
        fitTo(map, trees, fallback);
      }
      onFocus?.(tree);
      return;
    }
    if (trees.length === 0 && !fallback) return;
    fitted.current = true;
    fitTo(map, trees, fallback);
  }, [trees, fallback, ready, map, focusId, onFocus]);
  return null;
}

export default function MapaArboreo() {
  const [searchParams] = useSearchParams();
  const focusParam = searchParams.get("arvore");
  const focusId = focusParam && /^\d+$/.test(focusParam) ? Number(focusParam) : null;
  const [allTrees, setAllTrees] = useState<TreeMapPoint[]>([]);
  const [totalCadastrados, setTotalCadastrados] = useState(0);
  const [treesLoading, setTreesLoading] = useState(true);
  const [treesError, setTreesError] = useState<string | null>(null);

  const [filters, setFilters] = useState<Record<string, string>>({});
  const [layers, setLayers] = useState<MapLayer[]>(INITIAL_LAYERS);
  const [filterPanelOpen, setFilterPanelOpen] = useState(true);
  const [legendOpen, setLegendOpen] = useState(true);
  const [layerControlOpen, setLayerControlOpen] = useState(false);
  const [selectedTree, setSelectedTree] = useState<TreeMapPoint | null>(null);
  const [search, setSearch] = useState<{ query: string; type: string } | null>(null);
  const [activeQuickFilters, setActiveQuickFilters] = useState<string[]>([]);
  const [exporting, setExporting] = useState(false);
  const [locating, setLocating] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [baseGeo, setBaseGeo] = useState<Record<string, GeoJsonObject>>({});
  const baseRequested = useRef(new Set<string>());
  const [municipalStatus, setMunicipalStatus] = useState<MunicipalLayerStatus | null>(null);
  const handleFocus = useCallback((tree: TreeMapPoint | null) => {
    if (tree) setSelectedTree(tree);
    else setNotice("Exemplar sem coordenada ou fora do cadastro visível no mapa.");
  }, []);
  const mapRef = useRef<LeafletMap | null>(null);
  const mapAreaRef = useRef<HTMLDivElement | null>(null);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    const onChange = () => {
      setFullscreen(document.fullscreenElement === mapAreaRef.current);
      // o Leaflet so recalcula o tamanho do mapa quando avisado
      setTimeout(() => mapRef.current?.invalidateSize(), 150);
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else if (mapAreaRef.current?.requestFullscreen) {
      mapAreaRef.current.requestFullscreen().catch(() => setNotice("O navegador não permitiu a tela cheia."));
    } else {
      setNotice("Este navegador não oferece tela cheia.");
    }
  }, []);

  useEffect(() => {
    let active = true;
    api
      .trees()
      .then((rows) => {
        if (!active) return;
        setTotalCadastrados(rows.length);
        setAllTrees(rows.map(toTreeMapPoint).filter((t): t is TreeMapPoint => t !== null));
        setTreesError(null);
      })
      .catch((e: unknown) => {
        if (active) setTreesError(e instanceof Error ? e.message : "Não foi possível carregar os exemplares.");
      })
      .finally(() => {
        if (active) setTreesLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  // Tecnicos vem das vistorias reais, ligadas ao exemplar pelo codigo CTA.
  const { items: vistorias } = useInspectionsList();
  const tecnicosPorCodigo = useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const v of vistorias) {
      if (!v.codigoCta || !v.tecnico) continue;
      if (!m.has(v.codigoCta)) m.set(v.codigoCta, new Set());
      m.get(v.codigoCta)!.add(v.tecnico);
    }
    return m;
  }, [vistorias]);

  const { areasPiloto } = useAreasPiloto();
  const areaSbv = useMemo(() => areasPiloto.find((a) => a.code === SBV_AREA_PILOTO_CODE) ?? null, [areasPiloto]);
  const { quadras } = useQuadrasList(areaSbv?.id ?? null);
  const areaGeo = (areaSbv?.geom as GeoJsonObject | null) ?? null;
  const quadrasGeo = useMemo<GeoJsonObject | null>(() => {
    if (!areaSbv) return null;
    const features = quadras
      .filter((q) => q.areaPilotoId === areaSbv.id && q.geom)
      .map((q) => ({ type: "Feature" as const, geometry: q.geom, properties: { code: q.code } }));
    return features.length ? ({ type: "FeatureCollection", features } as unknown as GeoJsonObject) : null;
  }, [quadras, areaSbv]);

  const activeLayerIds = useMemo(() => new Set(layers.filter((l) => l.ativa).map((l) => l.id)), [layers]);

  // Camadas da base territorial de Goiania (ref_goiania): baixadas so quando ligadas pela primeira vez.
  useEffect(() => {
    const loaders: Record<string, () => Promise<unknown>> = {
      limite_municipal: api.municipalBoundaryGeojson,
      regioes: api.regionsGeojson,
      bairros: api.bairrosGeojson,
    };
    for (const id of Object.keys(loaders)) {
      if (!activeLayerIds.has(id) || baseRequested.current.has(id)) continue;
      baseRequested.current.add(id);
      loaders[id]()
        .then((geo) => setBaseGeo((prev) => ({ ...prev, [id]: geo as GeoJsonObject })))
        .catch(() => {
          baseRequested.current.delete(id);
          setNotice("Não foi possível carregar uma camada da base territorial.");
        });
    }
  }, [activeLayerIds]);

  const filterOptions = useMemo<MapFilterOptions>(() => {
    const uniq = (xs: string[]) => Array.from(new Set(xs.filter(Boolean))).sort((a, b) => a.localeCompare(b, "pt-BR"));
    const tecnicos = new Set<string>();
    for (const t of allTrees) tecnicosPorCodigo.get(t.codigoCta)?.forEach((n) => tecnicos.add(n));
    return {
      projetos: uniq(allTrees.map((t) => t.projeto)),
      bairros: uniq(allTrees.map((t) => t.bairro)),
      especies: uniq(allTrees.map((t) => t.nomeCientifico || t.nomePopular)),
      statusRevisao: uniq(allTrees.map((t) => t.statusRevisao)),
      tecnicos: Array.from(tecnicos).sort((a, b) => a.localeCompare(b, "pt-BR")),
    };
  }, [allTrees, tecnicosPorCodigo]);

  const filteredTrees = useMemo(() => {
    return allTrees.filter((tree) => {
      if (!activeLayerIds.has(layerOfTree(tree.tipo))) return false;
      if (filters.projeto && tree.projeto !== filters.projeto) return false;
      if (filters.bairro && tree.bairro !== filters.bairro) return false;
      if (filters.logradouro && !tree.logradouro.toLowerCase().includes(filters.logradouro.toLowerCase())) return false;
      if (filters.tipo && tree.tipo !== filters.tipo) return false;
      if (filters.especie && (tree.nomeCientifico || tree.nomePopular) !== filters.especie) return false;
      if (filters.classeRisco) {
        if (filters.classeRisco === "SEM_AVALIACAO" ? tree.classeRisco !== "" : tree.classeRisco !== filters.classeRisco) return false;
      }
      if (filters.statusRevisao && tree.statusRevisao !== filters.statusRevisao) return false;
      if (filters.tecnico && !tecnicosPorCodigo.get(tree.codigoCta)?.has(filters.tecnico)) return false;
      if (search) {
        const q = search.query.toLowerCase();
        const fields =
          search.type === "cta" ? [tree.codigoCta]
          : search.type === "especie" ? [tree.nomePopular, tree.nomeCientifico]
          : search.type === "logradouro" ? [tree.logradouro]
          : [tree.bairro];
        if (!fields.some((f) => f.toLowerCase().includes(q))) return false;
      }
      if (activeQuickFilters.length > 0) {
        const ok = activeQuickFilters.some((qf) => {
          switch (qf) {
            case "critico": return tree.classeRisco === "CRITICO";
            case "alto": return tree.classeRisco === "ALTO";
            case "sem_vistoria": return !tree.ultimaVistoriaId;
            default: return false;
          }
        });
        if (!ok) return false;
      }
      return true;
    });
  }, [allTrees, filters, activeLayerIds, search, activeQuickFilters, tecnicosPorCodigo]);

  const counts = useMemo(
    () => ({
      critico: filteredTrees.filter((t) => t.classeRisco === "CRITICO").length,
      alto: filteredTrees.filter((t) => t.classeRisco === "ALTO").length,
      semVistoria: filteredTrees.filter((t) => !t.ultimaVistoriaId).length,
    }),
    [filteredTrees]
  );

  const quickFilters = [
    { key: "all", label: "Todos", count: filteredTrees.length },
    { key: "critico", label: "Risco crítico", count: counts.critico },
    { key: "alto", label: "Risco alto", count: counts.alto },
    { key: "sem_vistoria", label: "Sem vistoria", count: counts.semVistoria },
  ];

  const handleClearFilters = useCallback(() => {
    setFilters({});
    setSearch(null);
    setActiveQuickFilters([]);
  }, []);

  const handleToggleLayer = useCallback((layerId: string) => {
    setLayers((prev) => prev.map((l) => (l.id === layerId ? { ...l, ativa: !l.ativa } : l)));
  }, []);

  const handleExport = useCallback(async () => {
    setExporting(true);
    setNotice(null);
    try {
      const fc = await api.treesGeoJson();
      const blob = new Blob([JSON.stringify(fc)], { type: "application/geo+json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `floraiq-exemplares-${new Date().toISOString().slice(0, 10)}.geojson`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: unknown) {
      setNotice(e instanceof Error ? `Falha ao exportar: ${e.message}` : "Falha ao exportar o GeoJSON.");
    } finally {
      setExporting(false);
    }
  }, []);

  const handleMyLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setNotice("Este navegador não informa a localização.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        mapRef.current?.setView([pos.coords.latitude, pos.coords.longitude], 18);
      },
      () => {
        setLocating(false);
        setNotice("Não foi possível obter a localização (permissão negada ou sinal indisponível).");
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  }, []);

  const hasActiveFilters = Object.keys(filters).length > 0 || !!search || activeQuickFilters.length > 0;
  const semCoordenada = totalCadastrados - allTrees.length;

  return (
    <div className="flex flex-col h-[calc(100dvh-6rem)] md:h-[calc(100dvh-7rem)] overflow-hidden">
      <MapToolbar
        totalArvores={allTrees.length}
        filteredCount={filteredTrees.length}
        loading={treesLoading}
        exporting={exporting}
        onExportGeoJSON={handleExport}
        onCenterMap={() => mapRef.current && fitTo(mapRef.current, filteredTrees, areaGeo)}
        onToggleLayers={() => setLayerControlOpen(!layerControlOpen)}
        layersOpen={layerControlOpen}
      />

      <div className="flex flex-1 overflow-hidden relative">
        <MapFilterPanel
          collapsed={!filterPanelOpen}
          onToggle={() => setFilterPanelOpen(!filterPanelOpen)}
          options={filterOptions}
          onApplyFilters={setFilters}
          onClearFilters={handleClearFilters}
        />

        <div ref={mapAreaRef} className="flex-1 relative overflow-hidden bg-background-100">
          <FloraIQMap
            center={SBV_CENTER}
            zoom={15}
            defaultBaseLayer="hybrid"
            showZoomControl={false}
            onMapReady={(map) => {
              mapRef.current = map;
            }}
          >
            <FitOnce
              trees={allTrees}
              fallback={areaGeo}
              ready={!treesLoading}
              focusId={focusId}
              onFocus={handleFocus}
            />

            <MapScaleAndPointer />

            {baseGeo.limite_municipal && activeLayerIds.has("limite_municipal") && (
              <GeoJSON
                key="limite_municipal"
                data={baseGeo.limite_municipal}
                style={{ color: "#111827", weight: 2.5, dashArray: "10 6", fill: false }}
                interactive={false}
              />
            )}

            {baseGeo.regioes && activeLayerIds.has("regioes") && (
              <GeoJSON
                key="regioes"
                data={baseGeo.regioes}
                style={{ color: "#b45309", weight: 2, fillColor: "#f59e0b", fillOpacity: 0.05 }}
                onEachFeature={(feature, layer) => {
                  const p = (feature.properties ?? {}) as { region?: string; bairros?: number; population_2022?: number };
                  layer.bindTooltip(
                    `Região ${p.region ?? ""}${p.bairros != null ? ` · ${p.bairros} bairros` : ""}${
                      p.population_2022 != null ? ` · ${p.population_2022.toLocaleString("pt-BR")} hab. (2022)` : ""
                    }`,
                    { sticky: true },
                  );
                }}
              />
            )}

            {baseGeo.bairros && activeLayerIds.has("bairros") && (
              <GeoJSON
                key="bairros"
                data={baseGeo.bairros}
                style={{ color: "#0891b2", weight: 1, fillOpacity: 0.02 }}
                onEachFeature={(feature, layer) => {
                  const p = (feature.properties ?? {}) as { name?: string; region?: string };
                  layer.bindTooltip(`${p.name ?? "Bairro"}${p.region ? ` · Região ${p.region}` : ""}`, { sticky: true });
                }}
              />
            )}

            {areaGeo && activeLayerIds.has("area_piloto") && (
              <GeoJSON
                key={`area-${areaSbv?.id}`}
                data={areaGeo}
                style={{ color: "#1B6E4F", weight: 2, dashArray: "6 4", fillOpacity: 0.04 }}
                interactive={false}
              />
            )}

            {quadrasGeo && activeLayerIds.has("quadras") && (
              <GeoJSON
                key={`quadras-${areaSbv?.id}-${quadras.length}`}
                data={quadrasGeo}
                style={{ color: "#2563eb", weight: 1, fillOpacity: 0.03 }}
                onEachFeature={(feature, layer) => {
                  const code = (feature.properties as { code?: string } | null)?.code;
                  if (code) layer.bindTooltip(`Quadra ${code}`, { sticky: true });
                }}
              />
            )}

            {activeLayerIds.has("arvores_municipais") && <MunicipalTreesLayer onStatus={setMunicipalStatus} />}

            {filteredTrees.map((tree) => (
              <Marker
                key={tree.id}
                position={[tree.latitude, tree.longitude]}
                icon={makeTreeIcon(tree.classeRisco, tree.tipo, selectedTree?.id === tree.id)}
                eventHandlers={{ click: () => setSelectedTree(tree) }}
              >
                <Popup>
                  <div className="text-xs min-w-[160px]">
                    <strong className="text-sm">{tree.nomePopular || "Espécie não identificada"}</strong>
                    {tree.nomeCientifico && <p className="text-gray-500 italic">{tree.nomeCientifico}</p>}
                    <p className="mt-1">
                      <span className="font-mono text-[10px] bg-gray-100 px-1 rounded">{tree.codigoCta}</span>
                    </p>
                    {tree.logradouro && <p className="mt-1">{tree.logradouro}</p>}
                    {tree.bairro && <p className="text-gray-500">{tree.bairro}</p>}
                    <span
                      className="mt-2 inline-block text-[10px] font-medium px-1.5 py-0.5 rounded"
                      style={{ background: riscoColor(tree.classeRisco) + "20", color: riscoColor(tree.classeRisco) }}
                    >
                      {tree.classeRisco ? `Risco ${riscoLabel(tree.classeRisco).toLowerCase()}` : "Sem avaliação de risco"}
                    </span>
                    <Link to={`/cta/${tree.id}`} className="mt-2 block text-center text-xs font-medium text-primary-600 hover:underline">
                      Ver FCA →
                    </Link>
                  </div>
                </Popup>
              </Marker>
            ))}
          </FloraIQMap>

          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] w-80">
            <MapSearchBar onSearch={(query, type) => setSearch({ query, type })} onClear={() => setSearch(null)} />
          </div>

          <div className="absolute top-16 left-1/2 -translate-x-1/2 z-[1000] flex gap-1.5">
            {quickFilters.map((qf) => (
              <button
                key={qf.key}
                onClick={() => {
                  if (qf.key === "all") setActiveQuickFilters([]);
                  else setActiveQuickFilters((prev) => (prev.includes(qf.key) ? prev.filter((f) => f !== qf.key) : [...prev, qf.key]));
                }}
                className={`text-[10px] font-medium px-2.5 py-1.5 rounded-full border transition-colors whitespace-nowrap ${
                  (qf.key === "all" && activeQuickFilters.length === 0) || activeQuickFilters.includes(qf.key)
                    ? "bg-accent-500 text-white border-accent-500"
                    : "bg-white/90 text-foreground-600 border-background-200 hover:bg-white"
                } shadow-sm`}
              >
                {qf.label}
                <span className="ml-1 opacity-70">({qf.count})</span>
              </button>
            ))}
          </div>

          <div className="absolute top-3 left-3 z-[1000]">
            <MapGISTools
              onZoomIn={() => mapRef.current?.zoomIn()}
              onZoomOut={() => mapRef.current?.zoomOut()}
              onCenter={() => mapRef.current && fitTo(mapRef.current, filteredTrees, areaGeo)}
              onToggleFullscreen={toggleFullscreen}
              fullscreen={fullscreen}
              onMyLocation={handleMyLocation}
              locating={locating}
            />
          </div>

          {notice && (
            <div className="absolute top-28 left-1/2 -translate-x-1/2 z-[1000] bg-warning-500 text-white text-[11px] font-medium px-3 py-1.5 rounded-full shadow-md whitespace-nowrap">
              {notice}
              <button onClick={() => setNotice(null)} className="ml-2 hover:opacity-70" aria-label="Fechar aviso">
                <i className="ri-close-line" />
              </button>
            </div>
          )}

          {/* Estado do cadastro: carregando, erro ou base vazia */}
          {!treesLoading && (treesError || allTrees.length === 0) && !hasActiveFilters && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[1000] bg-white/95 backdrop-blur-sm border border-background-200 rounded-card p-6 text-center shadow-lg max-w-sm">
              <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
                <i className={`${treesError ? "ri-error-warning-line" : "ri-plant-line"} text-foreground-400 text-xl`} aria-hidden="true" />
              </div>
              <p className="text-sm font-semibold text-foreground-800 font-heading">
                {treesError ? "Não foi possível carregar os exemplares" : "Nenhum exemplar com coordenada"}
              </p>
              <p className="text-xs text-foreground-500 mt-1">
                {treesError
                  ? treesError
                  : semCoordenada > 0
                    ? `${semCoordenada} exemplar(es) cadastrado(s) sem coordenada não aparecem no mapa.`
                    : "O cadastro ainda não tem exemplares. Use “Novo exemplar” para cadastrar o primeiro."}
              </p>
            </div>
          )}

          {filteredTrees.length === 0 && hasActiveFilters && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[1000] bg-white/95 backdrop-blur-sm border border-background-200 rounded-card p-6 text-center shadow-lg">
              <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
                <i className="ri-search-line text-foreground-400 text-xl" aria-hidden="true" />
              </div>
              <p className="text-sm font-semibold text-foreground-800 font-heading">Nenhum resultado encontrado</p>
              <p className="text-xs text-foreground-500 mt-1">Ajuste os filtros ou a busca para ver mais exemplares arbóreos.</p>
              <button onClick={handleClearFilters} className="mt-3 btn-outline text-xs py-1.5 px-4 whitespace-nowrap">
                Limpar todos os filtros
              </button>
            </div>
          )}

          {activeLayerIds.has("arvores_municipais") && municipalStatus && (
            <div className="absolute top-14 right-3 z-[999] max-w-[260px] bg-white/95 border border-background-200 rounded-card shadow-md px-3 py-2 text-[11px] text-foreground-700">
              <div className="flex items-center gap-1.5 font-semibold" style={{ color: MUNICIPAL_COLOR }}>
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: MUNICIPAL_COLOR }} aria-hidden="true" />
                Árvores da Prefeitura
              </div>
              <p className="mt-0.5">
                {municipalStatus.state === "loading" && "Carregando…"}
                {municipalStatus.state === "error" && municipalStatus.message}
                {municipalStatus.state === "ready" &&
                  (municipalStatus.mode === "clusters"
                    ? `${municipalStatus.count.toLocaleString("pt-BR")} na área visível, agrupadas. Aproxime para ver cada árvore.`
                    : `${municipalStatus.count.toLocaleString("pt-BR")} na área visível${municipalStatus.truncated ? " (parte delas; aproxime mais)" : ""}.`)}
              </p>
              <p className="mt-1 text-[10px] text-foreground-400">Levantamento 1998–2000. Referência, não é avaliação FloraIQ.</p>
            </div>
          )}

          <div className="absolute top-3 right-3 z-[1000]">
            <MapLayerControl layers={layers} onToggleLayer={handleToggleLayer} open={layerControlOpen} onClose={() => setLayerControlOpen(false)} />
          </div>

          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-[1000]">
            <MapStatsCards
              arvoresVisiveis={filteredTrees.length}
              riscoCritico={counts.critico}
              riscoAlto={counts.alto}
              semVistoria={counts.semVistoria}
            />
          </div>

          <div className="absolute bottom-3 right-3 z-[1000]">
            <MapLegend collapsed={!legendOpen} onToggle={() => setLegendOpen(!legendOpen)} />
          </div>
        </div>

        <MapDetailPanel
          selectedTree={selectedTree}
          tecnicos={selectedTree ? Array.from(tecnicosPorCodigo.get(selectedTree.codigoCta) ?? []) : []}
          open={!!selectedTree}
          onClose={() => setSelectedTree(null)}
        />
      </div>
    </div>
  );
}
